import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import "../styles/parkingVsTaxi.css";

const LHR_API = {
  flightSuggest: "/api/heathrow/flights/suggest",
  flightSchedules: "/api/heathrow/flights/schedules",
};

const buildLiveText = (live, { compact = false, fallbackMessage = null } = {}) => {
  if (!live) {
    if (!fallbackMessage) return null;
    return { parts: fallbackMessage, positionText: fallbackMessage };
  }
  const distance =
    typeof live.distance_km === "number"
      ? `~${live.distance_km} km${compact ? "" : ` (~${Math.round(live.distance_km * 0.621)} mi)`} from Heathrow`
      : null;
  const speed =
    typeof live.speed_kmh === "number" && live.speed_kmh > 0
      ? `~${live.speed_kmh} km/h${compact ? "" : ` (~${Math.round(live.speed_kmh * 0.621)} mph)`}`
      : null;
  const inAir = !live.on_ground && (live.altitude_m || 0) > 300;

  let positionText;
  if (!inAir) {
    positionText = "flight has not yet departed or has already landed";
  } else if (typeof live.distance_km === "number" && live.distance_km < 5 && live.speed_kmh < 60) {
    positionText = "very close to Heathrow, likely taxiing or on final approach";
  } else if (live.is_descending) {
    positionText = "in the air and descending towards Heathrow";
  } else if (live.is_climbing) {
    positionText = "in the air and climbing towards Heathrow";
  } else {
    positionText = "in the air near Heathrow";
  }
  const parts = compact
    ? [positionText.replace("towards Heathrow", ""), distance, speed].filter(Boolean).join(", ")
    : [positionText, distance, speed].filter(Boolean).join(" · ");
  return { parts, positionText };
};

const LiveLine = ({ live, compact = false, label = "Live position", fallbackMessage = null }) => {
  const data = buildLiveText(live, { compact, fallbackMessage });
  if (!data) return null;

  return (
    <p className="live-line">
      {label}: {data.parts || "tracking"}
      <span className="live-source" style={{ display: "block", marginTop: 2 }} />
    </p>
  );
};

const formatDuration = mins => {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const formatQueue = queue => {
  if (!queue) return null;
  const band = queue.wait_message || queue.wait_band_label || null;
  const updated = queue.snapshot_at
    ? new Date(queue.snapshot_at).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  return [band, updated ? `updated ${updated}` : null].filter(Boolean).join(" • ");
};

const queueSeverity = waitMinutes => {
  if (!Number.isFinite(waitMinutes)) return "neutral";
  if (waitMinutes > 60) return "warn";
  if (waitMinutes > 30) return "caution";
  return "good";
};

const badgeStyle = status => {
  if (status === "warn") return { background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c" };
  if (status === "caution") return { background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412" };
  if (status === "good") return { background: "#ecfdf3", border: "1px solid #bbf7d0", color: "#166534" };
  return { background: "#f8fafc", border: "1px solid #e5e7eb", color: "#111827" };
};

const queueBoxCopy = (queue, label) => {
  if (!queue) return { status: "neutral", text: `${label}: no recent snapshot` };
  const max = queue.max_wait_minutes ?? queue.min_wait_minutes ?? null;
  const status = queueSeverity(max);
  const descriptor =
    status === "warn"
      ? "Expect longer queues; allow extra buffer."
      : status === "caution"
      ? "Could be busy; allow some extra time."
      : "Typically manageable at this time band.";
  const waitText =
    max != null
      ? `${queue.min_wait_minutes ?? max}–${max} mins`
      : queue.wait_message || "Typical queues";
  return { status, text: `${label}: ${waitText}. ${descriptor}` };
};

const flightNumKey = n => String(n || "").replace(/\s+/g, "").toUpperCase();
const isLikelyFlightNumber = value => /^[A-Z0-9]{2,3}\d{1,4}$/.test(flightNumKey(value));
const scheduleAnchorTime = s =>
  s?.anchor_time_utc || s?.estimated_time_utc || s?.scheduled_anchor_utc || s?.scheduled_time_utc || null;

const scheduleDateKey = s => {
  const anchor = scheduleAnchorTime(s);
  if (!anchor) return String(s?.date || s?.snapshot_date || "").slice(0, 10);
  const d = new Date(anchor);
  if (Number.isNaN(d.getTime())) return String(s?.date || s?.snapshot_date || "").slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const year = parts.find(p => p.type === "year")?.value;
  const month = parts.find(p => p.type === "month")?.value;
  const day = parts.find(p => p.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : String(anchor).slice(0, 10);
};

const scheduleDelayMins = s => {
  const est = s?.estimated_time_utc ? new Date(s.estimated_time_utc).getTime() : NaN;
  const sch = s?.scheduled_anchor_utc || s?.scheduled_time_utc
    ? new Date(s.scheduled_anchor_utc || s.scheduled_time_utc).getTime()
    : NaN;
  if (!Number.isFinite(est) || !Number.isFinite(sch)) return null;
  return Math.round((est - sch) / 60000);
};

const dedupeSchedules = (items = [], directionLabel = "Arrival") => {
  const uniq = new Map();
  (Array.isArray(items) ? items : []).forEach(s => {
    const anchor = scheduleAnchorTime(s);
    if (!anchor) return;
    const dateKey = scheduleDateKey(s) || String(anchor).slice(0, 10);
    const key = `${directionLabel}|${dateKey}|${anchor}`;
    if (!uniq.has(key)) uniq.set(key, s);
  });
  return Array.from(uniq.values()).sort(
    (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
  );
};

const mapHeathrowSchedulesToBuckets = payload => {
  const rows = Array.isArray(payload?.schedules) ? payload.schedules : [];
  return {
    Arrival: rows.filter(r => String(r?.direction || "").toLowerCase() === "arrival"),
    Departure: rows.filter(r => String(r?.direction || "").toLowerCase() === "departure"),
  };
};

const formatScheduleLabel = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  });
};

const formatStatusMessage = flightData => {
  if (!flightData) return "";
  const scheduled = flightData.scheduled_local ? new Date(flightData.scheduled_local) : null;
  const base = flightData.status_message || "";
  if (!scheduled) return base;
  const now = new Date();
  if (now > scheduled && base.toLowerCase().startsWith("expected")) {
    return base.replace(/Expected/i, "Was expected");
  }
  return base;
};

const todayUpdatedLabel = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Europe/London",
}).format(new Date());

export default function FlightCheckerBase({
  terminal = null,
  defaultFlight = "",
  title,
  metaDescription,
  metaKeywords,
  canonical,
  ogTitle,
  ogDescription,
  schemaName,
  breadcrumbName,
  headerTitle,
  headerIntro,
  headerNote = "",
  inputPlaceholder,
  emptyInputMessage,
  notFoundMessage,
  resultHeading,
  pickupLabel,
  faqTitle,
  faqItems,
  toolsLinks = null,
}) {
  const [flight, setFlight] = useState(defaultFlight);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightFocused, setFlightFocused] = useState(false);
  const [flightActiveIdx, setFlightActiveIdx] = useState(-1);
  const [flightError, setFlightError] = useState("");
  const [flightSchedules, setFlightSchedules] = useState({ Arrival: [], Departure: [] });
  const [flightDates, setFlightDates] = useState([]);
  const [flightDirection, setFlightDirection] = useState("Arrival");
  const [flightDate, setFlightDate] = useState("");
  const [flightAnchorUtc, setFlightAnchorUtc] = useState("");
  const [flightDateError, setFlightDateError] = useState("");
  const flightTimer = useRef(null);
  const flightScheduleTimer = useRef(null);
  const flightScheduleRequestSeq = useRef(0);
  const flightListboxId = `flight-checker-flight-suggestions${terminal ? `-t${terminal}` : ""}`;

  const filterTerminalRows = items =>
    !terminal
      ? items
      : (items || []).filter(item => String(item?.terminal || "").replace("T", "") === String(terminal));

  const availableFlightDirections = [
    ...(flightSchedules.Arrival.length ? ["Arrival"] : []),
    ...(flightSchedules.Departure.length ? ["Departure"] : []),
  ];
  const resolvedFlightDirection = availableFlightDirections.includes(flightDirection)
    ? flightDirection
    : availableFlightDirections[0] || "Arrival";
  const shouldShowFlightDirection = availableFlightDirections.length > 1;

  const fetchFlightSuggestions = value => {
    const v = flightNumKey(value);
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      setFlightActiveIdx(-1);
      setFlightError(v.length === 0 ? "" : "Flight number format looks invalid.");
      return;
    }
    if (!isLikelyFlightNumber(v)) {
      setFlightError("Use flight format like BA566 or KU103.");
    } else {
      setFlightError("");
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get(LHR_API.flightSuggest, { params: { q: v, limit: 8 } });
        const list = resp.data?.suggestions || [];
        setFlightSuggestions(list.map(f => ({ flightNumber: f })));
        setFlightActiveIdx(-1);
      } catch {
        setFlightSuggestions([]);
        setFlightActiveIdx(-1);
      }
    }, 180);
  };

  const refreshFlightSchedules = (flightNum, { debounce = true, resetSelection = true, preferredDirection = null } = {}) => {
    const clean = flightNumKey(flightNum);
    if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    if (!clean || clean.length < 2) {
      setFlightSchedules({ Arrival: [], Departure: [] });
      setFlightDates([]);
      setFlightDateError("");
      if (resetSelection) {
        setFlightDirection("Arrival");
        setFlightDate("");
        setFlightAnchorUtc("");
      }
      return;
    }

    const runner = async () => {
      const requestId = ++flightScheduleRequestSeq.current;
      try {
        const lookupResp = await api.get(LHR_API.flightSchedules, {
          params: {
            flight_number: clean,
            past_hours: 24,
            future_days: 10,
          },
        });
        if (requestId !== flightScheduleRequestSeq.current) return;
        const mapped = mapHeathrowSchedulesToBuckets(lookupResp?.data || null);
        const arrival = dedupeSchedules(filterTerminalRows(mapped.Arrival || []), "Arrival");
        const departure = dedupeSchedules(filterTerminalRows(mapped.Departure || []), "Departure");
        const options = [
          ...(arrival.length ? ["Arrival"] : []),
          ...(departure.length ? ["Departure"] : []),
        ];
        const nextDirection = options.includes(preferredDirection) ? preferredDirection : options[0] || "Arrival";

        setFlightSchedules({ Arrival: arrival, Departure: departure });
        setFlightDirection(nextDirection);
        setFlightDateError(options.length ? "" : "No Heathrow flights found in the recent/past day and next few days.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      } catch {
        if (requestId !== flightScheduleRequestSeq.current) return;
        setFlightSchedules({ Arrival: [], Departure: [] });
        setFlightDates([]);
        setFlightDateError("Could not load Heathrow flight times right now.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      }
    };

    if (debounce) {
      flightScheduleTimer.current = setTimeout(runner, 180);
    } else {
      runner();
    }
  };

  const selectFlightSuggestion = value => {
    const val = flightNumKey(value);
    setFlight(val);
    setFlightSuggestions([]);
    setFlightActiveIdx(-1);
    setFlightFocused(false);
    setFlightDate("");
    setFlightAnchorUtc("");
    refreshFlightSchedules(val, { debounce: false, resetSelection: true });
  };

  const fetchFlightResult = async ({ flightNumber, direction, selectedFlightDate, anchorUtc }) => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await api.get(`/api/flights/by-number/${flightNumber}`, {
        params: {
          direction,
          flight_date: selectedFlightDate,
          anchor_utc: anchorUtc,
        },
      });
      const data = res.data.flight;
      const dataTerminal = String(data?.terminal || "").replace("T", "");
      if (terminal && dataTerminal && dataTerminal !== String(terminal)) {
        setError(
          `This flight is scheduled for Terminal ${dataTerminal}. Please use /terminal-${dataTerminal}-flight-checker or the main flight checker.`
        );
        return;
      }
      setResult(data);
      const url = new URL(window.location.href);
      url.searchParams.set("flight", flightNumber);
      url.searchParams.set("direction", direction);
      url.searchParams.set("date", selectedFlightDate);
      url.searchParams.set("anchor", anchorUtc);
      window.history.replaceState({}, "", url.toString());
    } catch {
      setError(notFoundMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const paramFlight = flightNumKey(search.get("flight") || "");
    const paramDirection = String(search.get("direction") || "").trim();
    const paramDate = String(search.get("date") || "").trim();
    const paramAnchor = String(search.get("anchor") || "").trim();

    if (paramFlight) {
      setFlight(paramFlight);
      if (paramDirection) setFlightDirection(paramDirection);
      if (paramDate) setFlightDate(paramDate);
      if (paramAnchor) setFlightAnchorUtc(paramAnchor);
      refreshFlightSchedules(paramFlight, {
        debounce: false,
        resetSelection: false,
        preferredDirection: paramDirection || null,
      });
      if (paramDirection && paramDate && paramAnchor) {
        fetchFlightResult({
          flightNumber: paramFlight,
          direction: paramDirection,
          selectedFlightDate: paramDate,
          anchorUtc: paramAnchor,
        });
      }
    } else if (defaultFlight) {
      refreshFlightSchedules(defaultFlight, {
        debounce: false,
        resetSelection: true,
      });
    }

    return () => {
      if (flightTimer.current) clearTimeout(flightTimer.current);
      if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    };
  }, []);

  useEffect(() => {
    const merged = [
      ...(flightSchedules.Arrival || []).map(s => ({ ...s, direction: "Arrival" })),
      ...(flightSchedules.Departure || []).map(s => ({ ...s, direction: "Departure" })),
    ].filter(s => s.direction === resolvedFlightDirection);

    const unique = new Map();
    merged.forEach(s => {
      const anchorIso = scheduleAnchorTime(s);
      if (!anchorIso) return;
      const dayKey = scheduleDateKey(s) || String(anchorIso).slice(0, 10);
      const key = `${s.direction}|${dayKey}|${anchorIso}`;
      if (!unique.has(key)) unique.set(key, s);
    });
    const nextDates = Array.from(unique.values()).sort(
      (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
    );
    setFlightDates(nextDates);

    if (!flightDate && nextDates.length === 1) {
      const onlyOption = nextDates[0];
      setFlightDirection(onlyOption.direction || resolvedFlightDirection);
      setFlightDate(scheduleDateKey(onlyOption) || "");
      setFlightAnchorUtc(String(scheduleAnchorTime(onlyOption) || ""));
    }

    if (flightDate && nextDates.length) {
      const hasSelection = nextDates.some(s => {
        const optionDate = scheduleDateKey(s);
        const optionAnchor = String(scheduleAnchorTime(s) || "");
        return String(optionDate) === String(flightDate) && String(optionAnchor) === String(flightAnchorUtc || "");
      });
      if (!hasSelection) {
        setFlightDate("");
        setFlightAnchorUtc("");
      }
    }
  }, [flightSchedules, resolvedFlightDirection, flightDate, flightAnchorUtc]);

  useEffect(() => {
    if (!flightFocused || flightSuggestions.length === 0) {
      if (flightActiveIdx !== -1) setFlightActiveIdx(-1);
      return;
    }
    if (flightActiveIdx >= flightSuggestions.length) {
      setFlightActiveIdx(flightSuggestions.length - 1);
    }
  }, [flightFocused, flightSuggestions, flightActiveIdx]);

  const handleFlightKeyDown = e => {
    if (!flightSuggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev + 1 > flightSuggestions.length - 1 ? 0 : prev + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev - 1 < 0 ? flightSuggestions.length - 1 : prev - 1));
      return;
    }
    if (e.key === "Enter" && flightActiveIdx >= 0) {
      e.preventDefault();
      const candidate = flightSuggestions[flightActiveIdx]?.flightNumber;
      if (candidate) selectFlightSuggestion(candidate);
    }
  };

  const handleSearch = async () => {
    if (!flightNumKey(flight)) {
      setError(emptyInputMessage);
      return;
    }
    if (!flightDate || !flightAnchorUtc) {
      setError("Select the exact Heathrow arrival/departure time before checking the flight.");
      return;
    }
    await fetchFlightResult({
      flightNumber: flightNumKey(flight),
      direction: resolvedFlightDirection,
      selectedFlightDate: flightDate,
      anchorUtc: flightAnchorUtc,
    });
  };

  const schemaGraph = [
    {
      "@type": "SoftwareApplication",
      name: schemaName,
      applicationCategory: "TravelApplication",
      operatingSystem: "Web",
      url: canonical,
      offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map(item => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.cheapcabgrab.com/" },
        { "@type": "ListItem", position: 2, name: breadcrumbName, item: canonical },
      ],
    },
  ];

  const journeyLinks = toolsLinks || [
    { href: "/train-vs-taxi", label: "Compare taxi vs train." },
    { href: "/heathrow-parking-vs-taxi", label: "See whether parking or taxi is cheaper." },
    { href: "/heathrow-taxi", label: "Book a Heathrow taxi transfer." },
    { href: "/terminalFinder", label: "Find your terminal." },
    { href: "/leave-for-heathrow", label: "When to leave for Heathrow" },
    { href: "/arrival-assistant", label: "Arrival assistant" },
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@graph": schemaGraph })}</script>
      </Helmet>

      <div className="parking-wrapper">
        <div className="parking-header">
          <h1>{headerTitle}</h1>
          <p style={{ fontSize: 13, color: "#475467", marginTop: 4, marginBottom: 6 }}>
            Updated: {todayUpdatedLabel}
          </p>
          <p>{headerIntro}</p>
          {headerNote ? <p style={{ fontSize: 13, color: "#475467" }}>{headerNote}</p> : null}
        </div>

        <div className="parking-form">
          <div className="form-group">
            <label>Enter Flight Number</label>
            <input
              type="text"
              className="parking-input"
              placeholder={inputPlaceholder}
              value={flight}
              onChange={e => {
                const val = e.target.value.toUpperCase();
                setFlight(val);
                setResult(null);
                setError("");
                setFlightDate("");
                setFlightAnchorUtc("");
                fetchFlightSuggestions(val);
                refreshFlightSchedules(val, { debounce: true, resetSelection: true });
              }}
              onKeyDown={handleFlightKeyDown}
              onFocus={() => setFlightFocused(true)}
              onBlur={() =>
                setTimeout(() => {
                  setFlightFocused(false);
                  setFlightActiveIdx(-1);
                }, 200)
              }
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={flightFocused && flightSuggestions.length > 0}
              aria-controls={flightListboxId}
              aria-activedescendant={
                flightFocused && flightActiveIdx >= 0 ? `flight-checker-flight-option-${terminal || "all"}-${flightActiveIdx}` : undefined
              }
            />
            {flightFocused && flightSuggestions.length > 0 && (
              <div className="suggestions" id={flightListboxId} role="listbox" aria-label="Flight number suggestions">
                {flightSuggestions.map((s, idx) => (
                  <button
                    type="button"
                    key={s.flightNumber}
                    className="suggestion"
                    id={`flight-checker-flight-option-${terminal || "all"}-${idx}`}
                    role="option"
                    aria-selected={idx === flightActiveIdx}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      background: "transparent",
                      border: 0,
                      cursor: "pointer",
                    }}
                    onMouseEnter={() => setFlightActiveIdx(idx)}
                    onMouseDown={e => {
                      e.preventDefault();
                      selectFlightSuggestion(s.flightNumber);
                    }}
                  >
                    {s.flightNumber}
                  </button>
                ))}
              </div>
            )}
            {flightError && <p style={{ color: "#b42318", marginTop: 8, marginBottom: 0 }}>{flightError}</p>}
          </div>

          {shouldShowFlightDirection && (
            <div className="form-group">
              <label>Flight Direction</label>
              <select
                className="parking-input"
                value={resolvedFlightDirection}
                onChange={e => {
                  const nextDirection = String(e.target.value || "Arrival");
                  setFlightDirection(nextDirection);
                  setFlightDate("");
                  setFlightAnchorUtc("");
                  setResult(null);
                  setError("");
                }}
              >
                {availableFlightDirections.includes("Arrival") && <option value="Arrival">Arrives at Heathrow</option>}
                {availableFlightDirections.includes("Departure") && <option value="Departure">Departs Heathrow</option>}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Flight Date / Time</label>
            <select
              className="parking-input"
              value={flightDate ? `${resolvedFlightDirection}|${flightDate}|${flightAnchorUtc || ""}` : ""}
              onChange={e => {
                const [dir, val, anchor] = String(e.target.value || "").split("|");
                setFlightDirection(dir || resolvedFlightDirection);
                setFlightDate(val || "");
                setFlightAnchorUtc(anchor || "");
                setResult(null);
                setError("");
                setFlightDateError("");
              }}
            >
              <option value="">Select exact Heathrow flight time</option>
              {flightDates.map(s => {
                const d = scheduleDateKey(s) || s.date || s.snapshot_date || "";
                const anchorIso = scheduleAnchorTime(s);
                const timeLabel = anchorIso ? formatScheduleLabel(anchorIso) : d || "—";
                const labelPrefix = s.direction === "Arrival" ? "Arrives at Heathrow — " : "Departs Heathrow — ";
                const terminalLabel = s.terminal ? ` • T${s.terminal}` : "";
                const delayMins = scheduleDelayMins(s);
                const delayLabel =
                  delayMins == null ? "" : delayMins > 0 ? ` • Delay +${delayMins}m` : delayMins < 0 ? ` • Early ${Math.abs(delayMins)}m` : "";
                const statusLabel = s.status ? ` • ${String(s.status)}` : "";
                return (
                  <option
                    key={`${flightNumKey(flight)}-${s.direction}-${d}-${anchorIso || ""}`}
                    value={`${s.direction}|${String(d).slice(0, 10)}|${anchorIso || ""}`}
                  >
                    {`${labelPrefix}${timeLabel}${terminalLabel}${delayLabel}${statusLabel}`}
                  </option>
                );
              })}
            </select>
            {flightDateError && <p style={{ color: "#b42318", marginTop: 8, marginBottom: 0 }}>{flightDateError}</p>}
          </div>

          <button className="parking-btn" onClick={handleSearch} disabled={loading || !!flightError || !flightDate || !flightAnchorUtc}>
            {loading ? "Checking Flight..." : "Check Flight"}
          </button>

          {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
        </div>

        {result && (
          <div className="parking-results">
            {(() => {
              const live = result.live_status || null;
              const reliability = result.reliability_summary || null;
              const isArrival = result.arrival_or_departure === "A";
              const immEea = result.queues?.immigration_eea || null;
              const immNonEea = result.queues?.immigration_noneea || null;
              const securityQueue = result.queues?.security || null;
              const queueBoxes = [];
              if (isArrival) {
                queueBoxes.push(queueBoxCopy(immEea, "Immigration (EEA/UK)"));
                queueBoxes.push(queueBoxCopy(immNonEea, "Immigration (Non-EEA)"));
              } else {
                queueBoxes.push(queueBoxCopy(securityQueue, "Security"));
              }
              return (
                <>
                  <div className="best-choice">
                    ✈️ {result.airline_name} {result.iata_flight_identifier}
                  </div>
                  {(reliability || queueBoxes.length > 0) && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "grid",
                        gridTemplateColumns: isArrival && queueBoxes.length > 1 ? "1fr 1fr 1fr" : "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      {reliability && (
                        <div
                          style={{
                            padding: "12px 14px",
                            borderRadius: 12,
                            fontSize: 14,
                            minHeight: 68,
                            ...badgeStyle(reliability.level),
                            borderWidth: 1,
                          }}
                        >
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>Flight Reliability</div>
                          <div style={{ fontSize: 13, marginBottom: 2 }}>{reliability.text || "Recent Heathrow timings for this flight."}</div>
                          {Number.isFinite(reliability.onTimeShare) && (
                            <div style={{ fontSize: 13, marginBottom: 2 }}>
                              In the past month, ~{Math.round(reliability.onTimeShare * 100)}% landed within ~15 minutes of schedule.
                            </div>
                          )}
                        </div>
                      )}
                      {queueBoxes.map((box, idx) => {
                        const boxTitle =
                          isArrival && queueBoxes.length > 1
                            ? idx === 0
                              ? "Immigration (EEA/UK)"
                              : "Immigration (Non-EEA)"
                            : "Security";
                        return (
                          <div
                            key={`queue-box-${idx}`}
                            style={{
                              padding: "12px 14px",
                              borderRadius: 12,
                              fontSize: 14,
                              minHeight: 68,
                              ...badgeStyle(box.status),
                              borderWidth: 1,
                            }}
                          >
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{boxTitle}</div>
                            <div style={{ fontSize: 13 }}>{box.text.replace(`${boxTitle}: `, "")}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <LiveLine live={live} compact label="Live position" />
                </>
              );
            })()}

            <h2 style={{ marginTop: 12 }}>{resultHeading}</h2>
            <table className="parking-table">
              <tbody>
                <tr>
                  <th>Type</th>
                  <td>{result.arrival_or_departure === "A" ? "Arrival" : "Departure"}</td>
                </tr>
                <tr>
                  <th>Terminal</th>
                  <td>{result.terminal ? `Terminal ${result.terminal}` : "Not available"}</td>
                </tr>
                <tr>
                  <th>From</th>
                  <td>{result.origin_city} ({result.origin_airport_iata})</td>
                </tr>
                <tr>
                  <th>To</th>
                  <td>{result.destination_city} ({result.destination_airport_iata})</td>
                </tr>
                <tr>
                  <th>Scheduled Time</th>
                  <td>{result.scheduled_local ? new Date(result.scheduled_local).toLocaleString() : "Not available"}</td>
                </tr>
                {Number.isFinite(result.scheduled_duration_minutes) && (
                  <tr>
                    <th>Scheduled Duration</th>
                    <td>{formatDuration(result.scheduled_duration_minutes)}</td>
                  </tr>
                )}
                {result.arrival_or_departure === "D" && (result.checkin_start_local || result.checkin_zone) && (
                  <tr>
                    <th>Check-in / Zone</th>
                    <td>
                      {result.checkin_start_local ? new Date(result.checkin_start_local).toLocaleString() : "—"}
                      {result.checkin_zone && ` (Zone ${result.checkin_zone})`}
                    </td>
                  </tr>
                )}
                {result.arrival_or_departure === "D" && result.gate_number && (
                  <tr>
                    <th>Gate</th>
                    <td>{result.gate_number}</td>
                  </tr>
                )}
                {result.status_message && (
                  <tr>
                    <th>Live Status</th>
                    <td>
                      <div>{formatStatusMessage(result)}</div>
                      <LiveLine
                        live={result.live_status || null}
                        label="Live position"
                        fallbackMessage="Flight has not yet departed or has already landed"
                      />
                    </td>
                  </tr>
                )}
                {result.arrival_or_departure === "A" && (
                  <>
                    <tr>
                      <th>Immigration (EEA)</th>
                      <td>{formatQueue(result.queues?.immigration_eea) || "—"}</td>
                    </tr>
                    <tr>
                      <th>Immigration (Non-EEA)</th>
                      <td>{formatQueue(result.queues?.immigration_noneea) || "—"}</td>
                    </tr>
                  </>
                )}
                {result.arrival_or_departure === "D" && (
                  <tr>
                    <th>Security</th>
                    <td>{formatQueue(result.queues?.security) || "—"}</td>
                  </tr>
                )}
                <tr>
                  <th>Aircraft</th>
                  <td>{result.aircraft_description || "Not available yet"}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: 18, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
              <strong>What should I do now?</strong>
              <p style={{ marginTop: 6, marginBottom: 6, lineHeight: 1.5 }}>
                {result.arrival_or_departure === "A"
                  ? "Passenger likely exits arrivals 45–75 minutes after landing. Wait nearby until they message you before entering Short Stay."
                  : "Your flight is a departure. If it’s already departed, nothing else is needed at the airport; otherwise, follow your airline’s check-in and security guidance."}
              </p>
              {pickupLabel ? (
                <a href="/heathrow-arrival-pickup-time" className="parking-btn" style={{ display: "inline-block", textDecoration: "none" }}>
                  {pickupLabel}
                </a>
              ) : null}
            </div>

            <div className="leave-section-card" style={{ marginTop: 18 }}>
              <h3 className="leave-section-title">{faqTitle}</h3>
              <ul style={{ marginTop: 8, lineHeight: 1.6, paddingLeft: 18 }}>
                {faqItems.map(item => (
                  <li key={item.question}>
                    <strong>{item.question}</strong> {item.answer}
                  </li>
                ))}
              </ul>
            </div>

            <div
              style={{
                marginTop: 18,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 12,
                background: "#f9fafb",
              }}
            >
              <strong>Heading to or from Heathrow? Try these tools:</strong>
              <ul style={{ marginTop: 8, lineHeight: 1.5, marginBottom: 0 }}>
                {journeyLinks.map(link => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#475467" }}>
              Live data is refreshed regularly. Always follow airline and airport instructions on the day.
            </div>

            <p className="parking-disclaimer">
              Flight information is based on our operational data captured, we update this regularly, but airport conditions can change quickly. Always confirm final times, gates and delays on the official Heathrow website or with your airline. Taxi journey times and costs are estimates only and can vary with traffic and route.
            </p>
          </div>
        )}

        <ReviewsBar />
      </div>
    </>
  );
}
